/**
 * 路由守卫
 * 未登录一律送回登录页；已登录但没有该页权限时给出明确说明，
 * 不要留白页——门店遇到白屏只会以为系统坏了。
 */
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth/use-auth';
import { findNavItem } from '@/app/nav-config';
import { EmptyState, LoadingBlock } from '@/components/ui/empty-state';

/**
 * 从路由地址解析出"这一页需要什么权限"。
 * 老板端和员工端各有一份页面清单，守卫本身不该知道哪一份是哪一份，
 * 所以由路由表在挂载时把对应的解析函数传进来（默认是老板端）。
 */
type NavItemLike = { permission?: string } | null;

/**
 * 这个账号能不能进管理端。
 *
 * **字段缺失时不能当成 false。** 部署不同步是常态：前端先发、API 容器还是旧镜像，
 * `canAccessConsole` 就会是 undefined，而 `!undefined` 为真——
 * 那会把**包括老板在内的所有人**踢去手机端，管理端整个进不去。
 * 这正是 §5.1 记过的 `defaultEntry` 事故的同一个形状，只是方向更糟。
 *
 * 所以缺失时退回同源的 `defaultEntry`：它和 `canAccessConsole` 由服务端同一个
 * 表达式算出，含义一致，只是上线更早。两个都没有才拦——那种情况下
 * 拿到的根本不是一个可用的登录上下文。
 */
function resolveConsoleAccess(user: {
  canAccessConsole?: boolean;
  defaultEntry?: 'console' | 'employee';
}): boolean {
  if (typeof user.canAccessConsole === 'boolean') return user.canAccessConsole;
  return user.defaultEntry !== 'employee';
}

export function RequireAuth({
  children,
  resolveNavItem = findNavItem,
  console: isConsole = false,
}: {
  children: ReactNode;
  resolveNavItem?: (pathname: string) => NavItemLike;
  /**
   * 这一组路由属于管理端。开着时会检查 `canAccessConsole`——
   * 员工角色只登录手机端，进不来管理端（产品 2026-08-05）。
   *
   * **只挂在管理端那一组上**：改密页和手机端不能带这个标记，
   * 否则员工连密码都改不了。
   */
  console?: boolean;
}) {
  const { initializing, user, can } = useAuth();
  const location = useLocation();

  // 启动时正在用本地 token 换 /me，先显示加载态，避免闪一下登录页
  if (initializing) {
    return (
      <div className="grid h-full place-items-center">
        <LoadingBlock label="正在恢复登录状态" />
      </div>
    );
  }

  if (!user) {
    // 记下原本要去的地址，登录后可以直接回到那一页
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // 初始密码只用于证明开户凭证已送达。业务页面必须在前后端同时拦截，不能让用户
  // 跳过首次改密继续使用一个可能已经被交付人员看过的密码。
  if (user.passwordChangeRequired && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  /**
   * 员工角色进不了管理端。
   *
   * **直接送去手机端，不是显示一个错误页**——员工在管理端没有任何事可做，
   * 停在这儿只能看着一句"没有权限"，还得自己知道要去输 `/m/today`。
   * 这个判断必须在下面的逐页权限检查之前：否则员工输一个管理端地址，
   * 看到的会是"缺某某权限"，像是配漏了一项，而不是"这一端不给你用"。
   */
  if (isConsole && !resolveConsoleAccess(user)) {
    return <Navigate to="/m/today" replace />;
  }

  const navItem = resolveNavItem(location.pathname);
  if (navItem?.permission && !can(navItem.permission)) {
    return (
      <EmptyState
        icon="lock"
        title="你的账号没有这一页的权限"
        hint={`需要「${navItem.permission}」权限。如果确实需要用到，请让店主在「员工与工资 → 花名册」里调整你的岗位权限。`}
      />
    );
  }

  return <>{children}</>;
}
